import createSdk from '@descope/web-js-sdk'
import { auth } from '../firebase'
import { buildEmergencyScope } from '../data/turkeyProvinces'

// Public project identifier supplied by the user; this is not a management key.
export const DESCOPE_PROJECT_ID = process.env.REACT_APP_DESCOPE_PROJECT_ID || 'P3JdYlOnBiCZwhfSEcELwpkZ5RZx'
const proofs = new WeakMap()
const fingerprint = (scope, provinceCodes) => JSON.stringify(buildEmergencyScope(scope, provinceCodes))
const normalizeEmail = (value) => (value || '').trim().toLowerCase()

function descopeError(response, fallback) {
  const code = response?.error?.errorCode || response?.code
  const description = response?.error?.errorDescription || response?.error?.errorMessage
  const details = [code && `Descope kodu: ${code}`, description].filter(Boolean).join(' — ')
  return new Error(details ? `${fallback} ${details}` : fallback)
}

function requestError(error) {
  if (error instanceof TypeError && /fetch|network/i.test(error.message || '')) {
    return new Error('Descope isteği tarayıcı tarafından engellendi. Descope Project ayarlarında Allowed Origins listesine bu uygulamanın adresini ekleyin.')
  }
  return error
}

export function createEmergencyOtp({ scope, provinceCodes, uid }) {
  const user = auth.currentUser
  if (!user || user.uid !== uid || user.isAnonymous || !user.email) throw new Error('E-posta adresi olan hesabınızla giriş yapın.')
  const email = normalizeEmail(user.email)
  const selectionKey = fingerprint(scope, provinceCodes)
  const sdk = createSdk({ projectId: DESCOPE_PROJECT_ID, persistTokens: false, autoRefresh: false, storeLastAuthenticatedUser: false })
  let active = true
  let sent = false
  let nextSendAt = 0
  let latestProof = null
  function assertSession() {
    if (!active || auth.currentUser?.uid !== uid || normalizeEmail(auth.currentUser?.email) !== email) throw new Error('Oturum değişti. Doğrulamayı yeniden başlatın.')
  }
  return {
    email,
    async send() {
      assertSession()
      if (Date.now() < nextSendAt) throw new Error('Yeni kod istemeden önce bir dakika bekleyin.')
      sent = false
      if (latestProof) proofs.delete(latestProof)
      // A request may have been delivered even if its response is lost.
      nextSendAt = Date.now() + 60000
      let response
      try {
        response = await sdk.otp.signUpOrIn.email(email)
      } catch (error) {
        throw requestError(error)
      }
      assertSession()
      if (!response.ok) {
        if (response.code === 429) throw new Error('Çok fazla kod istendi. Lütfen daha sonra tekrar deneyin.')
        throw descopeError(response, 'Kod gönderilemedi.')
      }
      sent = true
      return { maskedEmail: response.data?.maskedEmail || email, resendAt: nextSendAt }
    },
    async verify(code) {
      assertSession()
      if (!sent || !/^\d{6}$/.test(code)) throw new Error('E-postanıza gönderilen 6 haneli kodu girin.')
      let response
      try {
        response = await sdk.otp.verify.email(email, code)
      } catch (error) {
        throw requestError(error)
      }
      assertSession()
      if (!response.ok) {
        if (response.code === 429) throw new Error('Çok fazla deneme yapıldı. Lütfen daha sonra tekrar deneyin.')
        throw descopeError(response, 'Kod yanlış veya süresi dolmuş.')
      }
      sent = false
      const data = response.data
      if (!data?.sessionJwt || data.user?.verifiedEmail !== true || normalizeEmail(data.user.email) !== email) throw new Error('Doğrulanan e-posta mevcut hesabınızla eşleşmiyor.')
      const expiresAt = Math.min(Date.now() + 5 * 60000, Number(data.sessionExpiration) * 1000)
      if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) throw new Error('Doğrulama oturumunun süresi doldu. Yeni kod isteyin.')
      latestProof = Object.freeze({})
      proofs.set(latestProof, { uid, email, selectionKey, expiresAt, sessionToken: data.sessionJwt })
      return latestProof
    },
    dispose() {
      active = false
      sent = false
      if (latestProof) proofs.delete(latestProof)
    },
  }
}

// This guard only coordinates the UI. The API MUST validate both signed tokens,
// match verified email to Firebase identity, and enforce freshness/replay protection.
export function consumeEmergencyOtp(proof, { uid, scope, provinceCodes }) {
  const record = proof && proofs.get(proof)
  if (!record || record.uid !== uid || record.email !== normalizeEmail(auth.currentUser?.email)
    || record.selectionKey !== fingerprint(scope, provinceCodes) || record.expiresAt <= Date.now()) {
    throw new Error('Bu çağrı için e-posta OTP doğrulaması gerekiyor.')
  }
  proofs.delete(proof)
  return record.sessionToken
}
